import { useEffect, useRef } from 'react';
import { mergeCanvas, canvasPart, privateMerge, type Include } from '../lib/canvasContent';
import { ApiError, endpoints, type SharedCanvas } from '../services/api';
import { useLists, type TaskList } from '../store/lists';
import { registerFlush } from '../lib/syncFlush';
import { captureSnapshot } from '../store/presets';
import { useScreen } from '../store/screen';
import { useSession } from '../store/session';
import { pickSettings, syncableSettings, useSettings, type Settings } from '../store/settings';

// When signed in, keeps settings + lists in the user's account so every screen matches.
// Local data is kept per account (store/switchProfile.ts), so the copy uploaded for a new
// account is only ever that account's own. On sign-in the server copy wins if one exists.
// Later local changes are pushed (debounced); a 409 means another screen saved first,
// in which case its copy is applied here.
//
// What this screen shows depends on Settings → Screens:
// - the main setup (default);
// - a saved layout: shown instead of the main setup, and this screen's changes are saved
//   into that layout, leaving the main setup as it is;
// - a shared canvas ("shared:<id>"): the canvas, with the viewer's own private content
//   (lib/canvasContent.ts). Changes to the canvas's part are saved to it when the viewer
//   may edit it; changes to private content go to the viewer's own main setup.

interface SyncedDoc {
  settings: Settings;
  lists: TaskList[];
}

interface CanvasState {
  id: string;
  version: number;
  include: Include;
  edit: boolean;
  snapshot: Record<string, unknown>;
}

const sharedId = (assigned: string | null) => (assigned?.startsWith('shared:') ? assigned.slice('shared:'.length) : null);

export function useSettingsSync(): void {
  const userId = useSession((s) => s.user?.id ?? null);
  const accounts = useSession((s) => s.config?.features.accounts ?? false);
  const assigned = useScreen((s) => (userId ? (s.assignments[userId] ?? null) : null));
  const version = useRef<string | null>(null);
  const applyingRemote = useRef(false);
  /** The account's main setup as last downloaded (needed when this screen shows something else). */
  const main = useRef<Settings | null>(null);
  /** The shared canvas this screen shows, as last downloaded. */
  const canvas = useRef<CanvasState | null>(null);

  useEffect(() => {
    if (!userId || !accounts) return;
    let cancelled = false;
    let timer: number | undefined;
    let ready = false;
    const shared = sharedId(assigned);
    const presetId = shared ? null : assigned;
    // Each account has its own version; never reuse the previous account's.
    version.current = null;
    main.current = null;
    canvas.current = null;
    useScreen.getState().setCanvas(null);

    /** Puts on screen whatever this screen should show, from the latest downloads. */
    const show = (lists?: TaskList[]) => {
      const m = main.current;
      applyingRemote.current = true;
      if (m) {
        const c = canvas.current;
        const preset = presetId ? m.presets?.find((p) => p.id === presetId) : undefined;
        if (shared && c) useSettings.getState().replaceAll(mergeCanvas(c.snapshot, m, c.include));
        else if (preset?.snapshot) useSettings.getState().replaceAll({ ...preset.snapshot, presets: m.presets });
        else useSettings.getState().replaceAll(m);
      }
      if (lists) useLists.getState().replaceAll(lists);
      applyingRemote.current = false;
    };
    const applyDoc = (doc: Partial<SyncedDoc>) => {
      if (doc.settings) main.current = doc.settings;
      show(doc.lists);
    };
    const takeCanvas = (c: SharedCanvas) => {
      canvas.current = { id: c.id, version: c.version, include: c.include, edit: c.permissions.edit, snapshot: c.snapshot ?? {} };
      useScreen.getState().setCanvas({ id: c.id, name: c.name, ownerEmail: c.owner.email, edit: c.permissions.edit, mine: c.owner.me, include: c.include });
    };
    const loadCanvas = async (): Promise<boolean> => {
      if (!shared) return false;
      try {
        const { canvas: c } = await endpoints.canvas(shared);
        if (cancelled) return false;
        const changed = !canvas.current || canvas.current.version !== c.version;
        takeCanvas(c);
        return changed;
      } catch (e) {
        // No longer shared with this person (or deleted): show the main setup.
        if (!cancelled && e instanceof ApiError && e.status === 404 && canvas.current) {
          canvas.current = null;
          useScreen.getState().setCanvas(null);
          return true;
        }
        return false;
      }
    };

    /** What to save to the account: this screen's setup, saved layout or private content. */
    const accountDoc = (): SyncedDoc | null => {
      const local = useSettings.getState();
      const lists = useLists.getState().lists;
      // Showing a shared canvas: only private content goes to the account. (If the canvas is
      // gone, the main setup is on screen and saves as normal below.)
      if (shared && canvas.current) return main.current ? { settings: privateMerge(main.current, local, canvas.current.include), lists } : null;
      if (!presetId) return { settings: syncableSettings(pickSettings(local)), lists };
      if (!main.current) return null; // never upload a layout's look as the main setup
      const presets = local.presets.map((p) =>
        p.id === presetId ? { ...p, snapshot: captureSnapshot(local), savedAt: new Date().toISOString(), look: undefined } : p,
      );
      return { settings: { ...main.current, presets }, lists };
    };

    const pushCanvas = async () => {
      const c = canvas.current;
      if (!c?.edit) return; // can use it but not change it: nothing is saved to it
      try {
        const { canvas: saved } = await endpoints.saveCanvas(c.id, canvasPart(useSettings.getState(), c.include), c.version);
        if (!cancelled) takeCanvas(saved);
      } catch (e) {
        // Someone else saved first: show theirs.
        if (!cancelled && e instanceof ApiError && e.status === 409) {
          takeCanvas((e.body as { canvas: SharedCanvas }).canvas);
          show();
        }
      }
    };

    const push = async () => {
      if (shared) void pushCanvas();
      const doc = accountDoc();
      if (!doc) return;
      try {
        const res = await endpoints.putSettings(doc as unknown as Record<string, unknown>, version.current);
        if (!cancelled) {
          version.current = res.version;
          main.current = doc.settings;
        }
      } catch (e) {
        // A late reply for a previous account must not land in this one's stores.
        if (!cancelled && e instanceof ApiError && e.status === 409) {
          const body = e.body as { data: Partial<SyncedDoc> | null; version: string | null };
          version.current = body.version;
          if (body.data) applyDoc(body.data);
        }
      }
    };

    let pendingPush = false;
    const schedule = () => {
      if (!ready || applyingRemote.current) return;
      window.clearTimeout(timer);
      pendingPush = true;
      timer = window.setTimeout(() => {
        pendingPush = false;
        void push();
      }, 2000);
    };
    // Before leaving the page for a sign-in redirect: save a waiting change now.
    registerFlush(async () => {
      if (!pendingPush) return;
      window.clearTimeout(timer);
      pendingPush = false;
      await push();
    });

    (async () => {
      try {
        await loadCanvas();
        const remote = await endpoints.getSettings();
        if (cancelled) return;
        version.current = remote.version;
        if (remote.data) {
          applyDoc(remote.data as Partial<SyncedDoc>);
        } else {
          await push();
        }
      } catch {
        // offline – local copy keeps working; changes will push once a request succeeds
      }
      if (!cancelled) ready = true;
    })();

    const unsubSettings = useSettings.subscribe(schedule);
    const unsubLists = useLists.subscribe(schedule);
    // Pick up edits made on other screens.
    const poll = window.setInterval(async () => {
      try {
        const remote = await endpoints.getSettings();
        if (!cancelled && remote.version && remote.version !== version.current && remote.data) {
          version.current = remote.version;
          applyDoc(remote.data as Partial<SyncedDoc>);
        }
      } catch {
        /* ignore */
      }
    }, 5 * 60_000);
    // A shared canvas can be changed by other people: look for that every minute.
    const canvasPoll = shared
      ? window.setInterval(async () => {
          if (await loadCanvas()) show();
        }, 60_000)
      : undefined;

    return () => {
      cancelled = true;
      registerFlush(null);
      window.clearTimeout(timer);
      window.clearInterval(poll);
      window.clearInterval(canvasPoll);
      unsubSettings();
      unsubLists();
    };
    // A new assignment (or back to the main setup) re-runs this: download and apply afresh.
  }, [userId, accounts, assigned]);
}
